import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import FormComp from "../../../Componentes/FormComp";
import Overlay from "../../../Componentes/Overlay";
import Formulario from "../../../Componentes/Utils/Formulario";
import Modal from "../../../Componentes/Utils/Modal";
import { useModal } from "../../../contexts/ModalContext";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";

const ModalTitle = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    text-align: left;
    width: 100%;
`;

const STATUS_COLAB = {
    IDLE: "idle",
    LOADING: "loading",
    FOUND: "found",
    NOT_FOUND: "not_found",
};

const ModalNovoProssmap = ({ onGenerated }) => {

    const { closeModal } = useModal();
    const { notifyError, notifySuccess } = useToast();
    const api = useApi();

    const [matricula, setMatricula] = useState("");
    const [mesReferencia, setMesReferencia] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
    const [nomeEncarregado, setNomeEncarregado] = useState("");
    const [centroCusto, setCentroCusto] = useState("");
    const [contrato, setContrato] = useState("");
    const [equipe, setEquipe] = useState("");
    const [qtdSubordinados, setQtdSubordinados] = useState(null);
    const [statusColab, setStatusColab] = useState(STATUS_COLAB.IDLE);
    const [salvando, setSalvando] = useState(false);

    const mesAtual = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }, []);

    useEffect(() => {
        if (matricula.length !== 6) {
            setNomeEncarregado("");
            setCentroCusto("");
            setContrato("");
            setEquipe("");
            setQtdSubordinados(null);
            setStatusColab(STATUS_COLAB.IDLE);
            return;
        }

        let cancelled = false;

        const fetchColab = async () => {
            setStatusColab(STATUS_COLAB.LOADING);

            try {
                const { data } = await api.get(
                    `/Painel/API/Prossmap/GetEncarregadoInfo/index.php?matricula=${matricula}`
                );

                if (cancelled) return;

                if (!data?.success) {
                    setNomeEncarregado("");
                    setCentroCusto("");
                    setContrato("");
                    setEquipe("");
                    setQtdSubordinados(null);
                    setStatusColab(STATUS_COLAB.NOT_FOUND);
                    notifyError(data?.message || "Erro ao buscar colaborador.");
                    return;
                }

                setQtdSubordinados(data.qtd_subordinados ?? 0);

                if (data.found) {
                    setNomeEncarregado(data.nome ?? "");
                    setCentroCusto(data.centro_custo ?? "");
                    setContrato(data.contrato ?? "");
                    setEquipe(data.equipe ?? "");
                    setStatusColab(STATUS_COLAB.FOUND);
                } else {
                    setNomeEncarregado("");
                    setCentroCusto("");
                    setContrato("");
                    setEquipe("");
                    setStatusColab(STATUS_COLAB.NOT_FOUND);
                }
            } catch (err) {
                console.error(err);

                if (!cancelled) {
                    setNomeEncarregado("");
                    setCentroCusto("");
                    setContrato("");
                    setEquipe("");
                    setQtdSubordinados(null);
                    setStatusColab(STATUS_COLAB.NOT_FOUND);

                    const msg =
                        err?.response?.data?.message || "Erro ao buscar colaborador.";
                    notifyError(msg);
                }
            }
        };

        fetchColab();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matricula]);

    const nomeExibido = (() => {
        if (statusColab === STATUS_COLAB.LOADING) return "BUSCANDO...";
        if (statusColab === STATUS_COLAB.NOT_FOUND) return "NENHUM COLABORADOR ENCONTRADO";
        if (statusColab === STATUS_COLAB.FOUND) return nomeEncarregado;
        return "NOME COMPLETO:";
    })();

    const qtdExibida = (() => {
        if (statusColab === STATUS_COLAB.LOADING) return "BUSCANDO...";
        if (statusColab === STATUS_COLAB.IDLE) return "QUANTIDADE DE SUBORDINADOS:";
        return String(qtdSubordinados ?? 0);
    })();

    const centroCustoExibido = (() => {
        if (statusColab === STATUS_COLAB.LOADING) return "BUSCANDO...";
        if (statusColab === STATUS_COLAB.FOUND) return centroCusto || "-";
        return "CENTRO DE CUSTO:";
    })();

    const contratoExibido = (() => {
        if (statusColab === STATUS_COLAB.LOADING) return "BUSCANDO...";
        if (statusColab === STATUS_COLAB.FOUND) return contrato || "-";
        return "CONTRATO:";
    })();

    const equipeExibida = (() => {
        if (statusColab === STATUS_COLAB.LOADING) return "BUSCANDO...";
        if (statusColab === STATUS_COLAB.FOUND) return equipe || "-";
        return "EQUIPE:";
    })();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!matricula.trim()) {
            notifyError("Informe a matrícula do encarregado.");
            return;
        }

        if (matricula.length !== 6) {
            notifyError("A matrícula deve conter 6 dígitos.");
            return;
        }

        if (statusColab !== STATUS_COLAB.FOUND) {
            notifyError("Encarregado não encontrado. Verifique a matrícula informada.");
            return;
        }

        if (!mesReferencia) {
            notifyError("Informe o mês de referência.");
            return;
        }

        if (mesReferencia > mesAtual) {
            notifyError("Não é possível gerar PROSSMAP para um mês futuro.");
            return;
        }

        try {
            setSalvando(true);

            const { data } = await api.post("/Painel/API/Prossmap/Generate/index.php", {
                matricula: matricula.trim(),
                referencia_mes: mesReferencia,
            });

            if (data?.success) {
                notifySuccess(data?.message || "PROSSMAP gerado com sucesso.");
                if (onGenerated) onGenerated();
                closeModal();
                return;
            }

            notifyError(data?.message || "Não foi possível gerar o PROSSMAP.");
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.message || "Erro ao gerar PROSSMAP.";
            notifyError(msg);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Overlay state>
            <Modal header close={closeModal} size="md">
                <ModalTitle>NOVO PROSSMAP</ModalTitle>
                <Formulario onSubmit={handleSubmit}>
                    <FormComp
                        title="MATRÍCULA DO ENCARREGADO"
                        type="numberOnly"
                        valor={matricula}
                        changeFunc={(e) => setMatricula(e.target.value)}
                        placeholder="Ex.: 123456"
                        maxLength={6}
                        required
                    />
                    <FormComp
                        title="NOME DO ENCARREGADO"
                        type="text"
                        valor={nomeExibido}
                        readOnly
                        disabled
                        invalid={statusColab === STATUS_COLAB.NOT_FOUND}
                    />
                    <FormComp
                        title="CENTRO DE CUSTO"
                        type="text"
                        valor={centroCustoExibido}
                        readOnly
                        disabled
                    />
                    <FormComp
                        title="CONTRATO"
                        type="text"
                        valor={contratoExibido}
                        readOnly
                        disabled
                    />
                    <FormComp
                        title="EQUIPE"
                        type="text"
                        valor={equipeExibida}
                        readOnly
                        disabled
                    />
                    <FormComp
                        title="COLABORADORES SUBORDINADOS"
                        type="text"
                        valor={qtdExibida}
                        readOnly
                        disabled
                    />
                    <FormComp
                        title="MÊS DE REFERÊNCIA"
                        type="month"
                        valor={mesReferencia}
                        changeFunc={(e) => setMesReferencia(e.target.value)}
                        required
                    />
                    <FormComp
                        type="submit"
                        valor={salvando ? "GERANDO…" : "GERAR PROSSMAP"}
                        disabled={salvando}
                    />
                </Formulario>
            </Modal>
        </Overlay>
    );
};

export default ModalNovoProssmap;
